import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Container, 
  Grid, 
  Paper, 
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Tooltip,
  ListItemButton
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Public as PublicIcon,
  Assignment as AssignmentIcon,
  Storefront as StorefrontIcon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Group as GroupIcon,
  LocalShipping as LocalShippingIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Work as WorkIcon,
  Task as TaskIcon,
  Analytics as AnalyticsIcon,
  SupportAgent as SupportAgentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Inbox as InboxIcon,
  Assignment as TaskAssignmentIcon,
  Phone as PhoneIcon,
  Assessment as AssessmentIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

// Import employee dashboard pages (you'll need to create these)
// import EmployeeDashboardHome from './Dashboard';
// import EmployeeProfile from './Profile';
import EmployeeTasks from './Tasks';
// import EmployeeOrders from './Orders';
// import EmployeeSupport from './Support';
// import EmployeeSchedule from './Schedule';
// import EmployeeReports from './Reports';
import EmployeeSettings from './Settings';

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 1,
}));

const EmployeeDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [language, setLanguage] = useState('bn'); // 'bn' for Bangla, 'en' for English
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    handleLanguageMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    // In a real app, this would handle logout logic
    navigate('/login');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    navigate(menuItems[newValue].path);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleMobileTabChange = (newValue) => {
    setCurrentTab(newValue);
    setMobileDrawerOpen(false);
  };

  // Translations for employee dashboard
  const translations = {
    bn: {
      title: 'কর্মচারী ড্যাশবোর্ড',
      dashboard: 'ড্যাশবোর্ড',
      profile: 'প্রোফাইল',
      tasks: 'কাজসমূহ',
      categories: 'ক্যাটাগরিসমূহ',
      products: 'প্রডাক্ট লাইব্রেরী',
      orders: 'অর্ডার পরিচালনা',
      support: 'কাস্টমার সাপোর্ট',
      schedule: 'সময়সূচী',
      reports: 'রিপোর্ট',
      settings: 'সেটিংস',
      logout: 'লগআউট',
      myProfile: 'আমার প্রোফাইল',
      myTasks: 'আমার কাজ',
      todayTasks: 'আজকের কাজ',
      language: 'ভাষা',
      bangla: 'বাংলা',
      english: 'English'
    },
    en: {
      title: 'Employee Dashboard',
      dashboard: 'Dashboard',
      profile: 'Profile',
      tasks: 'Tasks',
      categories: 'Categories',
      products: 'Product Library',
      orders: 'Order Management',
      support: 'Customer Support',
      schedule: 'Schedule',
      reports: 'Reports',
      settings: 'Settings',
      logout: 'Logout',
      myProfile: 'My Profile',
      myTasks: 'My Tasks',
      todayTasks: 'Today\'s Tasks',
      language: 'Language',
      bangla: 'বাংলা',
      english: 'English'
    }
  };

  const t = translations[language];

  const menuItems = [
    { text: t.dashboard, icon: <DashboardIcon />, path: '/employees/dashboard' },
    { text: t.profile, icon: <PersonIcon />, path: '/employees/dashboard/profile' },
    { text: t.tasks, icon: <TaskIcon />, path: '/employees/dashboard/tasks' },
    { text: t.categories, icon: <CategoryIcon />, path: '/employees/dashboard/categories' },
    { text: t.products, icon: <InventoryIcon />, path: '/employees/dashboard/products' },
    { text: t.orders, icon: <ShoppingCartIcon />, path: '/employees/dashboard/orders' },
    { text: t.support, icon: <SupportAgentIcon />, path: '/employees/dashboard/support' },
    { text: t.schedule, icon: <ScheduleIcon />, path: '/employees/dashboard/schedule' },
    { text: t.reports, icon: <BarChartIcon />, path: '/employees/dashboard/reports' },
    { text: t.settings, icon: <SettingsIcon />, path: '/employees/dashboard/settings' },
  ];

  // Set current tab based on current path
  React.useEffect(() => {
    const currentIndex = menuItems.findIndex(item => {
      return item.path === location.pathname;
    });
    if (currentIndex !== -1) {
      setCurrentTab(currentIndex);
    }
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <AppBarStyled>
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {t.title}
          </Typography>
          
          {/* Employee Action Buttons - Hidden on Mobile */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            gap: 1, 
            mr: 2 
          }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<TaskIcon />}
              component={Link}
              to="/employees/dashboard/tasks"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              আমার কাজ
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              component={Link}
              to="/employees/dashboard/reports"
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              রিপোর্ট
            </Button>
          </Box>
          
          {/* Language Switcher */}
          <IconButton 
            color="inherit" 
            onClick={handleLanguageMenuOpen}
            sx={{ mr: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          
          {/* View Site Icon */}
          <IconButton 
            color="inherit" 
            component="a" 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ mr: 1 }}
          >
            <PublicIcon />
          </IconButton>
          
          <IconButton color="inherit">
            <Badge badgeContent={4} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>ক</Avatar>
          </IconButton>
          
          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            disableScrollLock={true}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t.myProfile}
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t.settings}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t.logout}
            </MenuItem>
          </Menu>
          
          {/* Language Menu */}
          <Menu
            anchorEl={languageMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            disableScrollLock={true}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('bn')}
              selected={language === 'bn'}
            >
              {t.bangla}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
            >
              {t.english}
            </MenuItem>
          </Menu>
        </Toolbar>
        
        {/* Navigation Tabs - Hidden on Mobile */}
        {!isMobile && (
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'rgba(255,255,255,0.2)',
            background: 'linear-gradient(45deg, #3d1789 30%, #0a6abd 90%)'
          }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: 'white' }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.8)',
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Tab 
                  key={item.text}
                  label={item.text} 
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ minWidth: 120 }}
                />
              ))}
            </Tabs>
          </Box>
        )}
      </AppBarStyled>

      {/* Mobile Navigation Drawer */}
      <SwipeableDrawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerClose}
        onOpen={() => setMobileDrawerOpen(true)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'white',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t.title}
            </Typography>
            <IconButton onClick={handleMobileDrawerClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        <List component="nav" sx={{ p: 1 }}>
          {menuItems.map((item, index) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleMobileNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Quick Actions in Mobile Drawer */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            দ্রুত কার্যক্রম
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* My Work Button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<TaskIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/tasks"
              onClick={handleMobileDrawerClose}
              sx={{ 
                justifyContent: 'flex-start',
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              আমার কাজ
            </Button>
            
            {/* Report Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/reports"
              onClick={handleMobileDrawerClose}
              sx={{ 
                justifyContent: 'flex-start',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'primary.light',
                  color: 'primary.dark'
                }
              }}
            >
              রিপোর্ট
            </Button>
            
            <Divider sx={{ my: 1 }} />
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<TaskAssignmentIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/tasks"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              নতুন কাজ দেখুন
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<InventoryIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/products"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              প্রডাক্ট যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/reports"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              রিপোর্ট দেখুন
            </Button>
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="menu"
          onClick={handleMobileDrawerToggle}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            display: { xs: 'flex', md: 'none' },
            background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)'
          }}
        >
          <MenuIcon />
        </Fab>
      )}
      
      {/* Main Content Area with Sidebar */}
      <Box sx={{ display: 'flex', pt: { xs: 8, md: 16 } }}>
        {/* Left Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              backgroundColor: 'white',
              borderRight: '1px solid #e0e0e0',
              minHeight: 'calc(100vh - 128px)',
              p: 2,
              position: 'sticky',
              top: 128,
            }}
          >
          {/* Quick Actions for Employees */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            দ্রুত কার্যক্রম
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<TaskAssignmentIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/tasks"
              sx={{ justifyContent: 'flex-start' }}
            >
              নতুন কাজ দেখুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<InventoryIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/products"
              sx={{ justifyContent: 'flex-start' }}
            >
              প্রডাক্ট যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/categories"
              sx={{ justifyContent: 'flex-start' }}
            >
              ক্যাটাগরি যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/support"
              sx={{ justifyContent: 'flex-start' }}
            >
              সাপোর্ট কল শুরু করুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ShoppingCartIcon />}
              fullWidth
              component={Link}
              to="/employees/dashboard/orders"
              sx={{ justifyContent: 'flex-start' }}
            >
              অর্ডার পরিচালনা
            </Button>
          </Box>
          
          {/* Today's Stats for Employee */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            আজকের পরিসংখ্যান
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                সম্পন্ন কাজ
              </Typography>
              <Typography variant="h4" color="primary">
                ৮
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                আজকে যোগ করা প্রডাক্ট
              </Typography>
              <Typography variant="h4" color="success.main">
                ১২
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                আজকের অর্ডার
              </Typography>
              <Typography variant="h4" color="info.main">
                ২৫
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                সাপোর্ট কল
              </Typography>
              <Typography variant="h4" color="warning.main">
                ৬
              </Typography>
            </Paper>
          </Box>
          
          {/* Recent Task Alerts */}
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 3, 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            জরুরি বিজ্ঞপ্তি
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                প্রডাক্ট আপডেট
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ৫টি প্রডাক্ট অনুমোদনের অপেক্ষায়
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f3e5f5' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                নতুন ক্যাটাগরি
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ২টি নতুন ক্যাটাগরি রিভিউ প্রয়োজন
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#e8f5e8' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                জরুরি অর্ডার
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ৩টি অর্ডার জরুরি প্রসেসিং প্রয়োজন
              </Typography>
            </Paper>
          </Box>
        </Box>
        )}
        
        {/* Main Content */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            minHeight: 'calc(100vh - 128px)',
            p: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tasks" element={<EmployeeTasks />} />
            <Route path="/settings" element={<EmployeeSettings />} />
            {/* Add more routes for employee pages when they are created
            <Route path="/profile" element={<EmployeeProfile />} />
            <Route path="/categories" element={<EmployeeCategories />} />
            <Route path="/products" element={<EmployeeProducts />} />
            <Route path="/orders" element={<EmployeeOrders />} />
            <Route path="/support" element={<EmployeeSupport />} />
            <Route path="/schedule" element={<EmployeeSchedule />} />
            <Route path="/reports" element={<EmployeeReports />} />
            */}
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

// Employee Dashboard Home Component
const Dashboard = () => {
  // Mock data with employee-relevant content
  const myRecentTasks = [
    { id: 1, title: 'গ্রাহক অর্ডার যাচাই', type: 'অর্ডার ম্যানেজমেন্ট', priority: 'উচ্চ', status: 'সম্পন্ন', dueDate: 'আজ', completedAt: '১০:৩০ AM' },
    { id: 2, title: 'নতুন পণ্য ক্যাটালগ আপডেট', type: 'ইনভেন্টরি', priority: 'মাঝারি', status: 'চলমান', dueDate: 'আগামীকাল', progress: 75 },
    { id: 3, title: 'গ্রাহক সহায়তা কল', type: 'কাস্টমার সাপোর্ট', priority: 'উচ্চ', status: 'অপেক্ষমান', dueDate: 'আজ ২:০০ PM', customer: 'রহিমা বেগম' },
    { id: 4, title: 'সাপ্তাহিক রিপোর্ট প্রস্তুতি', type: 'রিপোর্টিং', priority: 'নিম্ন', status: 'নতুন', dueDate: 'শুক্রবার', assignedBy: 'ম্যানেজার সাহেব' },
  ];

  const todayOrders = [
    { id: 1, orderId: '#ORD-1001', customer: 'আহমেদ হাসান', store: 'তাজা সবজি দোকান', status: 'প্রসেসিং', amount: 850, time: '৯:১৫ AM', assignedEmployee: 'আমি' },
    { id: 2, orderId: '#ORD-1002', customer: 'ফাতেমা খাতুন', store: 'মাংস প্যারাডাইজ', status: 'নিশ্চিত', amount: 1200, time: '১০:৩০ AM', assignedEmployee: 'সাকিব' },
    { id: 3, orderId: '#ORD-1003', customer: 'করিম উদ্দিন', store: 'ফলের রাজ্য', status: 'প্যাকিং', amount: 650, time: '১১:৪৫ AM', assignedEmployee: 'আমি' },
  ];

  const mySchedule = [
    { id: 1, time: '৯:০০ AM', task: 'দৈনিক অর্ডার রিভিউ', type: 'মিটিং', status: 'সম্পন্ন' },
    { id: 2, time: '১১:০০ AM', task: 'গ্রাহক সহায়তা কল', type: 'সাপোর্ট', status: 'চলমান' },
    { id: 3, time: '২:০০ PM', task: 'টিম মিটিং', type: 'মিটিং', status: 'আসন্ন' },
    { id: 4, time: '৪:০০ PM', task: 'রিপোর্ট সাবমিট', type: 'কাজ', status: 'আসন্ন' },
  ];

  return (
    <Box>
      {/* First Div - Employee Statistics Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {/* Employee-specific Statistics Cards */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <TaskIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    আজকের কাজ
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>১২</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    সম্পন্ন কাজ
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৮</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <InventoryIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    প্রডাক্ট যোগ করেছি
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৪৮</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <CategoryIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    ক্যাটাগরি যোগ করেছি
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>১২</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <ShoppingCartIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    আজকের অর্ডার
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>২৫</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <SupportAgentIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    সাপোর্ট কল
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৬</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    পারফরমেন্স
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৯২%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <WorkIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মাসিক রেটিং
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৪.৮</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Second Div - Main Content Sections for Employee */}
      <Box>
        <Grid container spacing={2}>
          {/* My Recent Tasks */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  আমার সাম্প্রতিক কাজ
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<AddIcon />}
                  component={Link}
                  to="/employees/dashboard/tasks"
                >
                  সব দেখুন
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>কাজের নাম</TableCell>
                      <TableCell>অগ্রাধিকার</TableCell>
                      <TableCell>অবস্থা</TableCell>
                      <TableCell align="right">সময়সীমা</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myRecentTasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {task.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {task.type}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={task.priority} 
                            color={
                              task.priority === 'উচ্চ' ? 'error' :
                              task.priority === 'মাঝারি' ? 'warning' : 'info'
                            } 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={task.status} 
                            color={
                              task.status === 'সম্পন্ন' ? 'success' :
                              task.status === 'চলমান' ? 'primary' :
                              task.status === 'অপেক্ষমান' ? 'warning' : 'default'
                            } 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell align="right">{task.dueDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Today's Orders Assigned to Me */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  আজকের অর্ডার
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  component={Link}
                  to="/employees/dashboard/orders"
                >
                  সব দেখুন
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>অর্ডার আইডি</TableCell>
                      <TableCell>গ্রাহক</TableCell>
                      <TableCell>অবস্থা</TableCell>
                      <TableCell align="right">পরিমাণ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.orderId}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.customer}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.store}
                            </Typography>
                            {order.assignedEmployee === 'আমি' && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip 
                                  label="আমার দায়িত্ব"
                                  size="small"
                                  icon={<PersonIcon />}
                                  sx={{ 
                                    bgcolor: '#e8f5e8',
                                    color: '#2e7d32',
                                    fontSize: '0.6rem',
                                    height: '18px',
                                    '& .MuiChip-icon': {
                                      fontSize: '10px'
                                    }
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status} 
                            color={
                              order.status === 'নিশ্চিত' ? 'success' :
                              order.status === 'প্যাকিং' ? 'info' :
                              order.status === 'প্রসেসিং' ? 'primary' : 'warning'
                            } 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell align="right">৳{order.amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* My Today's Schedule */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                আজকের সময়সূচী
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {mySchedule.map((schedule) => (
                  <Paper key={schedule.id} sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {schedule.task}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {schedule.type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {schedule.time}
                        </Typography>
                      </Box>
                      <Chip 
                        label={schedule.status} 
                        color={
                          schedule.status === 'সম্পন্ন' ? 'success' :
                          schedule.status === 'চলমান' ? 'primary' : 'warning'
                        } 
                        size="small" 
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export { Dashboard as default, EmployeeDashboard };
